import { formatISO } from "date-fns";
import { PencilIcon } from "lucide-react";
import { useState } from "react";
import { SubmitHandler } from "react-hook-form";
import { useOutletContext } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useMutation } from "urql";

import { RoundForm } from "@/components/forms/RoundForm";
import { RoundInput, zRoundInput } from "@/components/forms/validation/round";
import { buttonVariants } from "@/components/ui/button";
import { DestructiveConfirmationAlertDialog } from "@/components/ui/destructive-confirmation-alert-dialog";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";
import { graphql } from "@/gen";
import { RoundT } from "@/routes/round/queries";
import { cn } from "@/utils";

const updateRoundMutation = graphql(`
    mutation updateRound($input: UpdateRoundInput!) {
        updateRound(input: $input) {
            __typename
            ...UseRound
        }
    }
`);

const deleteRoundMutation = graphql(`
    mutation deleteRound($roundId: UUID!) {
        deleteRound(roundId: $roundId)
    }
`);

export const RoundEditDialog = () => {
    const [_res, updateRound] = useMutation(updateRoundMutation);
    const [_resDelete, deleteRound] = useMutation(deleteRoundMutation);
    const { round } = useOutletContext<{
        round: RoundT;
    }>();

    const navigate = useNavigate();

    const [isOpen, setIsOpen] = useState(false);
    const [deleteAlertOpen, setDeleteAlertOpen] = useState(false);

    const onSubmit: SubmitHandler<RoundInput> = async input => {
        const roundSyncerConfig =
            input.roundSyncerConfig == null
                ? undefined
                : {
                      ...input.roundSyncerConfig,
                      pgnProviderType: input.roundSyncerConfig.pgnProviderType.toLowerCase(),
                      pollStart: input.roundSyncerConfig.pollStart
                          ? formatISO(input.roundSyncerConfig.pollStart)
                          : undefined,
                      pollEnd: input.roundSyncerConfig.pollEnd
                          ? formatISO(input.roundSyncerConfig.pollEnd)
                          : undefined,
                  };
        const result = await updateRound({
            input: {
                id: round.id,
                title: input.title,
                scheduledStartAt: input.scheduledStartAt.toISOString(),
                lastGameScheduledStartAt: input.lastGameScheduledStartAt?.toISOString() ?? "none",
                roundSyncerConfig,
                evaluationSurgeProtectionOverride: input.evaluationSurgeProtectionOverride,
            },
        });

        if (result.error) {
            toast.error("Unable to update round", { description: result.error.message });
            for (const error of result.error.graphQLErrors) {
                // @ts-expect-error typed as string from the API
                form.setError(error.name, { message: error.message });
            }
        } else {
            toast.success("Round was updated");
            setIsOpen(false);
        }
    };

    const handleDelete = async () => {
        const result = await deleteRound({ roundId: round.id });

        if (result.error) {
            toast.error("Unable to delete round", {
                description: result.error.message,
            });
        } else {
            toast.success("Round was deleted");
            setIsOpen(false);
            navigate(
                `/tournaments/${round.tournamentStage.tournament.id}/stages/${round.tournamentStage.id}`
            );
        }
    };

    const onDelete = () => {
        setDeleteAlertOpen(true);
    };

    const defaultValues = zRoundInput.parse({
        ...round,
        evaluationSurgeProtectionOverride: round.evaluationSurgeProtectionOverride,
        roundSyncerConfig: round.roundSyncerConfig
            ? {
                  ...round.roundSyncerConfig,
                  pollPgnSource: round.roundSyncerConfig.pollPgnSource || undefined,
                  streamingPgnSource: round.roundSyncerConfig.streamingPgnSource || undefined,
              }
            : undefined,
    });

    return (
        <>
            <Dialog open={isOpen} onOpenChange={() => setIsOpen(!isOpen)}>
                <DialogTrigger className={cn(buttonVariants())}>
                    <PencilIcon />
                </DialogTrigger>
                <DialogContent
                    className="max-h-[80vh] w-full max-w-3xl overflow-y-auto"
                    aria-describedby={undefined}
                >
                    <DialogHeader>
                        <div className="mr-4 flex items-center justify-between">
                            <DialogTitle>Edit round</DialogTitle>
                        </div>
                    </DialogHeader>
                    <RoundForm
                        onSubmit={onSubmit}
                        defaultValues={defaultValues}
                        onDelete={onDelete}
                    />
                </DialogContent>
            </Dialog>

            <DestructiveConfirmationAlertDialog
                open={deleteAlertOpen}
                onOpenChange={setDeleteAlertOpen}
                onConfirm={handleDelete}
                title="Delete Round"
                description="Are you sure you want to delete this round? This action cannot be undone."
            />
        </>
    );
};
